import type { Build, Defaults, Environment, EventDefinitions, Game, IngestKey, ParamSpec, SdkConfig } from '@growth/client';
import { type FormEvent, useState } from 'react';
import { NavLink, useParams } from 'react-router';
import { PageHeader } from '../components/Layout.tsx';
import { ConnectionsTab } from './Connections.tsx';
import { TargetsTab } from './Targets.tsx';
import { CreativeCatalogTab } from './CreativeCatalog.tsx';
import { Card, CopyButton, DataTable, download, ErrorBox, Secret } from '../components/ui.tsx';
import { ENV_LABEL } from '../filters.ts';
import { fmtDateTime } from '../format.ts';
import { invalidateCache, useAction, useApi, useSession } from '../session.tsx';

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'connections', label: 'Conexiones' },
  { id: 'targets', label: 'Objetivos' },
  { id: 'creative', label: 'Catálogo creativo' },
  { id: 'sdk', label: 'SDK Unity' },
  { id: 'keys', label: 'Claves de ingesta' },
  { id: 'builds', label: 'Builds' },
  { id: 'events', label: 'Eventos' },
  { id: 'privacy', label: 'Privacidad' },
];

const ENVS: Environment[] = ['development', 'staging', 'production'];

function GeneralTab({ game, reload }: { game: Game; reload: () => void }) {
  const { api, isAdmin } = useSession();
  const defaults = useApi<Defaults>('defaults', (a) => a.defaults());
  const action = useAction();
  const [notes, setNotes] = useState<string[]>([]);
  const [form, setForm] = useState({
    name: game.name,
    reporting_timezone: game.reporting_timezone,
    platforms: game.platforms,
    event_packs: game.event_packs,
    churn_days: game.settings.churn_days,
    max_event_age_days: game.settings.max_event_age_days,
    raw_event_retention_days: game.settings.raw_event_retention_days,
    require_registered_builds: game.settings.require_registered_builds,
    android_package: game.store_ids.android ?? '',
    reporting_currency: game.settings.reporting_currency ?? 'EUR',
    target_cpi: game.settings.target_cpi === null || game.settings.target_cpi === undefined ? '' : String(game.settings.target_cpi),
    store_fee_pct: String(Math.round((game.settings.iap_store_fee_rate ?? 0.15) * 1000) / 10),
    tax_pct: game.settings.iap_tax_rate === null || game.settings.iap_tax_rate === undefined ? '' : String(Math.round(game.settings.iap_tax_rate * 1000) / 10),
  });
  const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const r = await action.run(() =>
      api.updateGame(game.id, {
        name: form.name,
        reporting_timezone: form.reporting_timezone,
        platforms: form.platforms,
        event_packs: form.event_packs,
        store_ids: { ...game.store_ids, ...(form.android_package.trim() ? { android: form.android_package.trim() } : {}) },
        settings: {
          churn_days: Number(form.churn_days),
          max_event_age_days: Number(form.max_event_age_days),
          raw_event_retention_days: Number(form.raw_event_retention_days),
          require_registered_builds: form.require_registered_builds,
          reporting_currency: form.reporting_currency.trim().toUpperCase(),
          target_cpi: form.target_cpi === '' ? null : Number(form.target_cpi),
          iap_store_fee_rate: Number(form.store_fee_pct) / 100,
          iap_tax_rate: form.tax_pct === '' ? null : Number(form.tax_pct) / 100,
        },
      }),
    );
    if (r) {
      setNotes(r.notes.length ? r.notes : ['Guardado.']);
      invalidateCache('games');
      reload();
    }
  };

  const archive = async () => {
    if (!window.confirm(game.archived_at ? 'Reactivar el juego?' : 'Archivar el juego? Dejará de aparecer en el selector (los datos se conservan).')) return;
    if (await action.run(() => api.updateGame(game.id, { archived: !game.archived_at }))) {
      invalidateCache('games');
      reload();
    }
  };

  return (
    <Card title="Juego" subtitle="La zona horaria define qué es «un día» en todas las métricas.">
      <form className="stack" onSubmit={save}>
        <div className="form-grid">
          <label className="field">
            <span>Nombre</span>
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={!isAdmin} />
          </label>
          <label className="field">
            <span>Zona horaria (IANA)</span>
            <input className="input" value={form.reporting_timezone} onChange={(e) => setForm({ ...form, reporting_timezone: e.target.value })} disabled={!isAdmin} />
          </label>
          <label className="field">
            <span>Días sin jugar para contar abandono</span>
            <input className="input" type="number" min={1} max={90} value={form.churn_days} onChange={(e) => setForm({ ...form, churn_days: Number(e.target.value) })} disabled={!isAdmin} />
          </label>
          <label className="field">
            <span>Antigüedad máx. de evento aceptado (días)</span>
            <input className="input" type="number" min={1} max={400} value={form.max_event_age_days} onChange={(e) => setForm({ ...form, max_event_age_days: Number(e.target.value) })} disabled={!isAdmin} />
          </label>
          <label className="field">
            <span>Paquete Android (Google Play)</span>
            <input className="input" value={form.android_package} onChange={(e) => setForm({ ...form, android_package: e.target.value })} disabled={!isAdmin} placeholder="com.hiveinteractive.antpocalypse" />
          </label>
          <label className="field">
            <span>Moneda de los informes (ISO-4217)</span>
            <input className="input" maxLength={3} pattern="[A-Za-z]{3}" value={form.reporting_currency} onChange={(e) => setForm({ ...form, reporting_currency: e.target.value.toUpperCase() })} disabled={!isAdmin} />
          </label>
          <label className="field">
            <span>CPI objetivo ({form.reporting_currency})</span>
            <input className="input" type="number" min={0} step="0.01" value={form.target_cpi} onChange={(e) => setForm({ ...form, target_cpi: e.target.value })} disabled={!isAdmin} placeholder="sin objetivo" />
          </label>
          <label className="field" title="Google Play: 15 % del primer millón de USD al año (los programas de 2026 lo cambian por región: compruébalo en Play Console). Solo afecta al neto estimado de las compras.">
            <span>Comisión de la tienda en compras (%)</span>
            <input className="input" type="number" min={0} max={50} step="0.1" value={form.store_fee_pct} onChange={(e) => setForm({ ...form, store_fee_pct: e.target.value })} disabled={!isAdmin} />
          </label>
          <label className="field" title="IVA / impuesto medio incluido en los precios de tus mercados (p. ej. 21 en España, 16 en México). Vacío: el neto estimado no descuenta impuestos.">
            <span>Impuesto medio incluido en precios (%)</span>
            <input className="input" type="number" min={0} max={50} step="0.1" value={form.tax_pct} onChange={(e) => setForm({ ...form, tax_pct: e.target.value })} disabled={!isAdmin} placeholder="sin descontar" />
          </label>
          <label className="field">
            <span>Retención de eventos en bruto (días)</span>
            <input className="input" type="number" min={30} max={3650} value={form.raw_event_retention_days} onChange={(e) => setForm({ ...form, raw_event_retention_days: Number(e.target.value) })} disabled={!isAdmin} />
          </label>
        </div>
        <label className="check">
          <input type="checkbox" checked={form.require_registered_builds} onChange={(e) => setForm({ ...form, require_registered_builds: e.target.checked })} disabled={!isAdmin} />
          Avisar en Data Health de builds no registradas por CI
        </label>
        <div className="row">
          <span className="muted small">Plataformas:</span>
          {(defaults.data?.platforms ?? game.platforms).map((p) => (
            <label key={p} className="check">
              <input type="checkbox" checked={form.platforms.includes(p)} onChange={() => setForm({ ...form, platforms: toggle(form.platforms, p) })} disabled={!isAdmin} />
              {p}
            </label>
          ))}
        </div>
        <div className="row">
          <span className="muted small">Paquetes de eventos:</span>
          {(defaults.data?.packs ?? []).map((p) => (
            <label key={p.name} className="check" title={p.description}>
              <input type="checkbox" checked={form.event_packs.includes(p.name)} disabled={!isAdmin || p.name === 'core'} onChange={() => setForm({ ...form, event_packs: toggle(form.event_packs, p.name) })} />
              {p.name}
            </label>
          ))}
        </div>
        {action.error && <ErrorBox error={action.error} />}
        {notes.map((n) => (
          <div key={n} className="notice">
            {n}
          </div>
        ))}
        {isAdmin && (
          <div className="row">
            <button className="btn primary" type="submit" disabled={action.busy}>
              Guardar
            </button>
            <button className="btn danger" type="button" onClick={archive} disabled={action.busy}>
              {game.archived_at ? 'Reactivar' : 'Archivar juego'}
            </button>
          </div>
        )}
      </form>
    </Card>
  );
}

function SdkTab({ gameId }: { gameId: string }) {
  const cfg = useApi<SdkConfig>(`sdk:${gameId}`, (a) => a.sdkConfig(gameId));
  const c = cfg.data;
  const installLine = `"com.hiveinteractive.gamegrowth": "https://github.com/KGonzalezz/Game-Growth-OS.git?path=/unity/com.hiveinteractive.gamegrowth"`;
  const consent = `// Tras tu pantalla de consentimiento (o CMP con IAB TCF: Growth.ApplyTcfConsent()):
Growth.SetConsent(new GrowthConsent(
    analytics: ConsentStatus.Granted,
    ads: ConsentStatus.Granted,
    adPersonalization: ConsentStatus.Denied,
    adUserData: ConsentStatus.Denied));`;
  return (
    <div className="stack">
      {cfg.error && <ErrorBox error={cfg.error} />}
      {c && (
        <Card title="Valores para GameGrowthSettings" subtitle="Window → Game Growth → Settings. Las claves de ingesta son publicables (viajan en el APK); identifican juego y entorno.">
          <dl className="kv">
            <dt>Game Id</dt>
            <dd>
              <Secret value={c.game_id} />
            </dd>
            <dt>Api Base Url</dt>
            <dd>
              <Secret value={c.api_base_url} />
            </dd>
            {ENVS.map((env) => (
              <div key={env} style={{ display: 'contents' }}>
                <dt>{ENV_LABEL[env]} Key</dt>
                <dd>{c.ingest_keys[env] ? <Secret value={c.ingest_keys[env]!} /> : <span className="muted">sin clave activa</span>}</dd>
              </div>
            ))}
            <dt>Paquetes</dt>
            <dd>{c.event_packs.join(', ')}</dd>
          </dl>
        </Card>
      )}
      <Card title="Integración en 5 pasos">
        <ol className="stack" style={{ margin: 0, paddingLeft: 18 }}>
          <li>
            Añade el paquete en <code>Packages/manifest.json</code> (fija la versión añadiendo <code>#&lt;tag o commit&gt;</code>; repo privado = credenciales git) o usa «Add package from tarball» con el .tgz de <code>pnpm unity:pack</code>:
            <div className="row" style={{ marginTop: 6 }}>
              <code className="ellipsis" style={{ maxWidth: '100%' }}>
                {installLine}
              </code>
              <CopyButton text={installLine} />
            </div>
          </li>
          <li>
            Abre <strong>Window → Game Growth → Settings</strong> y pega los valores de arriba. Editor y builds de desarrollo envían a <em>Desarrollo</em>; los release a <em>Producción</em>. Para un APK de pruebas usa el define <code>GAMEGROWTH_ENV_STAGING</code>.
          </li>
          <li>
            Pide consentimiento antes de enviar nada (por defecto el SDK no escribe ni envía eventos sin consentimiento de analítica):
            <pre className="json" style={{ marginTop: 6 }}>{consent}</pre>
          </li>
          <li>
            Instrumenta los momentos clave con una línea cada uno: <code>Growth.Runs.Start(…)</code>, <code>Growth.Runs.End(…)</code>, <code>Growth.Tutorial.Step(…)</code>, <code>Growth.Ads.Completed(AdFormat.Rewarded, …)</code>. Ver la muestra «Survivor integration» del paquete.
          </li>
          <li>
            Dale a Play y abre <NavLink to={`/g/${gameId}/live`}>Eventos en vivo</NavLink> (entorno Desarrollo) y <strong>Window → Game Growth → Debugger</strong> para ver la cola local y copiar el JSON.
          </li>
        </ol>
      </Card>
    </div>
  );
}

function KeysTab({ gameId }: { gameId: string }) {
  const { api, isAdmin } = useSession();
  const keys = useApi<IngestKey[]>(`keys:${gameId}`, (a) => a.listKeys(gameId));
  const action = useAction();
  const [env, setEnv] = useState<Environment>('development');
  const [label, setLabel] = useState('');
  const [policy, setPolicy] = useState<'correct_skew' | 'trust_client'>('correct_skew');
  const refresh = () => {
    invalidateCache(`sdk:${gameId}`);
    keys.reload();
  };
  return (
    <div className="stack">
      <Card title="Claves de ingesta" subtitle="Rótalas creando una nueva, publicando la build y revocando la anterior. Una clave revocada deja de aceptar eventos al instante.">
        {keys.error && <ErrorBox error={keys.error} />}
        <DataTable
          data={{
            columns: [{ label: 'Entorno' }, { label: 'Clave' }, { label: 'Etiqueta' }, { label: 'Timestamps' }, { label: 'Creada' }, { label: 'Último uso' }, { label: '' }],
            rows: (keys.data ?? []).map((k) => [
              <span key="e" className={`badge env-${k.environment}`}>
                {ENV_LABEL[k.environment]}
              </span>,
              k.revoked_at ? <span key="k" className="muted mono">{k.key.slice(0, 12)}… (revocada)</span> : <Secret key="k" value={k.key} />,
              k.label ?? '—',
              k.timestamp_policy === 'trust_client' ? 'confía en el cliente' : 'corrige desfase',
              fmtDateTime(k.created_at, { seconds: false }),
              fmtDateTime(k.last_used_at, { seconds: false }),
              isAdmin && !k.revoked_at ? (
                <button
                  key="r"
                  type="button"
                  className="btn small danger"
                  onClick={async () => window.confirm('Revocar esta clave? Las builds que la usen dejarán de enviar datos.') && (await action.run(() => api.revokeKey(gameId, k.id))) && refresh()}
                >
                  Revocar
                </button>
              ) : (
                ''
              ),
            ]),
          }}
        />
      </Card>
      {isAdmin && (
        <Card title="Nueva clave" subtitle="«Confía en el cliente» acepta timestamps antiguos (hasta 400 días): solo para importaciones o simuladores.">
          <form
            className="form-grid"
            onSubmit={async (e) => {
              e.preventDefault();
              if (await action.run(() => api.createKey(gameId, { environment: env, label: label || undefined, timestamp_policy: policy }))) {
                setLabel('');
                refresh();
              }
            }}
          >
            <label className="field">
              <span>Entorno</span>
              <select className="input" value={env} onChange={(e) => setEnv(e.target.value as Environment)}>
                {ENVS.map((x) => (
                  <option key={x} value={x}>
                    {ENV_LABEL[x]}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Etiqueta</span>
              <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="build 1.31 / simulador" />
            </label>
            <label className="field">
              <span>Timestamps</span>
              <select className="input" value={policy} onChange={(e) => setPolicy(e.target.value as 'correct_skew' | 'trust_client')}>
                <option value="correct_skew">Corregir desfase del reloj (recomendado)</option>
                <option value="trust_client">Confiar en el cliente</option>
              </select>
            </label>
            <div>
              <button className="btn primary" type="submit" disabled={action.busy}>
                Crear clave
              </button>
            </div>
          </form>
          {action.error && <ErrorBox error={action.error} />}
        </Card>
      )}
    </div>
  );
}

function BuildsTab({ gameId }: { gameId: string }) {
  const { api, isAdmin } = useSession();
  const builds = useApi<Build[]>(`builds:${gameId}`, (a) => a.listBuilds(gameId));
  const action = useAction();
  const [form, setForm] = useState({ platform: 'android', app_version: '', build_number: '', commit_sha: '', channel: '' });
  return (
    <div className="stack">
      <Card title="Builds" subtitle="Registradas por CI (growth build register) o vistas en eventos. Sirven para filtrar por versión y detectar builds desconocidas.">
        {builds.error && <ErrorBox error={builds.error} />}
        <DataTable
          data={{
            columns: [{ label: 'Plataforma' }, { label: 'Versión' }, { label: 'Build' }, { label: 'Registrada' }, { label: 'Commit' }, { label: 'Canal' }, { label: 'Vista por primera vez' }, { label: 'Última vez' }],
            rows: (builds.data ?? []).map((b) => [
              b.platform,
              b.app_version,
              b.build_number || '—',
              b.registered ? 'sí' : 'no (solo vista)',
              b.commit_sha ? <code key="c">{b.commit_sha.slice(0, 10)}</code> : '—',
              b.channel ?? '—',
              fmtDateTime(b.first_seen_at, { seconds: false }),
              fmtDateTime(b.last_seen_at, { seconds: false }),
            ]),
          }}
        />
      </Card>
      {isAdmin && (
        <Card title="Registrar build">
          <form
            className="form-grid"
            onSubmit={async (e) => {
              e.preventDefault();
              const body = Object.fromEntries(Object.entries(form).filter(([, v]) => v)) as unknown as Parameters<typeof api.registerBuild>[1];
              if (await action.run(() => api.registerBuild(gameId, body))) {
                setForm({ ...form, app_version: '', build_number: '', commit_sha: '' });
                builds.reload();
              }
            }}
          >
            {(['platform', 'app_version', 'build_number', 'commit_sha', 'channel'] as const).map((k) => (
              <label key={k} className="field">
                <span>{k}</span>
                <input className="input" value={form[k]} required={k === 'platform' || k === 'app_version'} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              </label>
            ))}
            <div>
              <button className="btn primary" type="submit" disabled={action.busy}>
                Registrar
              </button>
            </div>
          </form>
          {action.error && <ErrorBox error={action.error} />}
        </Card>
      )}
    </div>
  );
}

const PARAM_EXAMPLE = `[
  { "name": "chest_id", "type": "string", "required": true },
  { "name": "gems", "type": "integer" }
]`;

function EventsTab({ gameId }: { gameId: string }) {
  const { api, isAdmin } = useSession();
  const defs = useApi<EventDefinitions>(`defs:${gameId}`, (a) => a.eventDefinitions(gameId));
  const action = useAction();
  const [form, setForm] = useState({ event_name: '', version: 1, description: '', params: PARAM_EXAMPLE });
  const d = defs.data;
  return (
    <div className="stack">
      <Card title="Eventos del catálogo" subtitle={d ? `Paquetes activos: ${d.packs.join(', ')} · la referencia completa está en docs/generated/event-reference.md` : undefined}>
        {defs.error && <ErrorBox error={defs.error} />}
        <DataTable
          data={{
            columns: [{ label: 'Evento' }, { label: 'v' }, { label: 'Descripción' }, { label: 'Parámetros' }],
            rows: (d?.catalog_events ?? []).map((e) => [
              <code key="n">{e.name}</code>,
              e.version,
              e.description,
              <span key="p" className="muted small">
                {e.params.map((p: ParamSpec) => `${p.name}${p.required ? '*' : ''}`).join(', ')}
              </span>,
            ]),
          }}
        />
      </Card>
      <Card title="Eventos propios del juego" subtitle="Inmutables: para cambiar parámetros registra una versión nueva (los análisis antiguos no se rompen).">
        <DataTable
          data={{
            columns: [{ label: 'Evento' }, { label: 'Versión' }, { label: 'Descripción' }, { label: 'Parámetros' }, { label: 'Creado' }],
            rows: (d?.custom_events ?? []).map((e) => [
              <code key="n">{e.event_name}</code>,
              e.version,
              e.description,
              <span key="p" className="muted small">
                {e.params.map((p) => `${p.name}:${p.type}${p.required ? '*' : ''}`).join(', ')}
              </span>,
              fmtDateTime(e.created_at, { seconds: false }),
            ]),
          }}
        />
        {d && d.custom_events.length === 0 && <div className="empty">Aún no hay eventos propios.</div>}
      </Card>
      {isAdmin && (
        <Card title="Registrar evento propio">
          <form
            className="stack"
            onSubmit={async (e) => {
              e.preventDefault();
              let params: ParamSpec[];
              try {
                params = JSON.parse(form.params || '[]') as ParamSpec[];
              } catch {
                action.setError('Los parámetros deben ser JSON válido.');
                return;
              }
              if (await action.run(() => api.createEventDefinition(gameId, { event_name: form.event_name, version: Number(form.version), description: form.description, params }))) {
                setForm({ ...form, event_name: '', description: '' });
                defs.reload();
              }
            }}
          >
            <div className="form-grid">
              <label className="field">
                <span>Nombre (snake_case)</span>
                <input className="input" value={form.event_name} required pattern="[a-z][a-z0-9_]{0,39}" onChange={(e) => setForm({ ...form, event_name: e.target.value })} />
              </label>
              <label className="field">
                <span>Versión</span>
                <input className="input" type="number" min={1} value={form.version} onChange={(e) => setForm({ ...form, version: Number(e.target.value) })} />
              </label>
              <label className="field">
                <span>Descripción</span>
                <input className="input" value={form.description} required onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </label>
            </div>
            <label className="field">
              <span>Parámetros (JSON) · tipos: string, integer, number, boolean, timestamp, uuid, string_array, object, array</span>
              <textarea className="input" rows={6} value={form.params} onChange={(e) => setForm({ ...form, params: e.target.value })} />
            </label>
            {action.error && <ErrorBox error={action.error} />}
            <div>
              <button className="btn primary" type="submit" disabled={action.busy}>
                Registrar evento
              </button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}

function PrivacyTab({ gameId }: { gameId: string }) {
  const { api, isAdmin } = useSession();
  const action = useAction();
  const [id, setId] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const valid = /^[0-9a-f-]{36}$/i.test(id.trim());
  return (
    <Card
      title="Solicitudes RGPD"
      subtitle="Acceso (exportar) y supresión (borrar) por installation_id. El SDK también puede pedir el borrado con Growth.RequestDataDeletion(). Tras borrar se guarda solo un hash para descartar eventos rezagados."
    >
      {!isAdmin && <div className="notice">Requiere un token con scope admin.</div>}
      <div className="row">
        <input className="input" style={{ minWidth: 320 }} value={id} onChange={(e) => setId(e.target.value)} placeholder="installation_id (uuid)" aria-label="installation_id" />
        <button
          type="button"
          className="btn"
          disabled={!isAdmin || !valid || action.busy}
          onClick={async () => {
            const r = await action.run(() => api.exportInstallation(gameId, id.trim()));
            if (r) {
              download(`${gameId}-${id.trim()}.json`, r);
              setResult(`Exportadas ${Object.values(r.data).reduce((s, v) => s + v.length, 0)} filas.`);
            }
          }}
        >
          Exportar
        </button>
        <button
          type="button"
          className="btn danger"
          disabled={!isAdmin || !valid || action.busy}
          onClick={async () => {
            if (!window.confirm('Borrar todos los datos de esta instalación en todos los entornos? No se puede deshacer.')) return;
            const r = await action.run(() => api.deleteInstallation(gameId, id.trim()));
            if (r) setResult(`Borrado (${r.request.id}): ${Object.entries(r.rows_deleted).map(([k, v]) => `${k} ${v}`).join(', ')}`);
          }}
        >
          Borrar
        </button>
      </div>
      {action.error && <ErrorBox error={action.error} />}
      {result && (
        <div className="notice" style={{ marginTop: 10 }}>
          {result}
        </div>
      )}
    </Card>
  );
}

export function SettingsPage() {
  const { gameId = '', tab = 'general' } = useParams();
  const game = useApi<Game>(`game:${gameId}`, (a) => a.getGame(gameId));
  return (
    <>
      <PageHeader title="Configuración" sub={game.data ? `${game.data.name} · ${game.data.id}` : undefined} />
      <nav className="tabs" aria-label="Secciones de configuración">
        {TABS.map((t) => (
          <NavLink key={t.id} to={`/g/${gameId}/settings/${t.id}`} className={() => (tab === t.id ? 'active' : '')}>
            {t.label}
          </NavLink>
        ))}
      </nav>
      {game.error && <ErrorBox error={game.error} onRetry={game.reload} />}
      {tab === 'general' && game.data && <GeneralTab key={game.data.updated_at} game={game.data} reload={game.reload} />}
      {tab === 'connections' && game.data && <ConnectionsTab game={game.data} />}
      {tab === 'targets' && game.data && <TargetsTab game={game.data} />}
      {tab === 'creative' && <CreativeCatalogTab gameId={gameId} />}
      {tab === 'sdk' && <SdkTab gameId={gameId} />}
      {tab === 'keys' && <KeysTab gameId={gameId} />}
      {tab === 'builds' && <BuildsTab gameId={gameId} />}
      {tab === 'events' && <EventsTab gameId={gameId} />}
      {tab === 'privacy' && <PrivacyTab gameId={gameId} />}
    </>
  );
}
